import { Anchor, Group } from '@mantine/core';
import { IconSparkles } from '@tabler/icons-react';
import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { PROVENANCE_KEYS } from './provenance.utils';
import { useTransactionProvenanceController } from './useTransactionProvenanceController';

interface Props {
	dealId: string;
	transactionId: string | undefined;
}

/** "From batch "…" · Created · Confirmed by … on … · 2 documents", on a transaction a confirm wrote; else nothing. */
export const TransactionProvenanceLine = ({ dealId, transactionId }: Props) => {
	const tCapManage = useTranslationCapManage();
	const { line } = useTransactionProvenanceController({ dealId, transactionId });
	if (line === null) {
		return null;
	}

	const title = line.batchTitle ?? tCapManage(PROVENANCE_KEYS.untitledBatch);
	const parts: Array<{ key: string; node: ReactNode }> = [
		{
			key: 'batch',
			node: (
				<Anchor
					component={Link}
					to={line.batchPath}
					data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_PROVENANCE_BATCH_LINK}
				>
					{tCapManage(PROVENANCE_KEYS.fromBatch, { title })}
				</Anchor>
			),
		},
		{ key: 'outcome', node: tCapManage(line.outcomeKey) },
		{
			key: 'confirmed',
			node: tCapManage(PROVENANCE_KEYS.confirmedBy, {
				name: line.confirmedBy,
				date: line.confirmedOn,
			}),
		},
		...(line.documentCount > 0
			? [
					{
						key: 'documents',
						node: tCapManage(PROVENANCE_KEYS.documents, { count: line.documentCount }),
					},
				]
			: []),
	];

	return (
		<Group
			component="p"
			gap="xs"
			wrap="nowrap"
			m={0}
			py="xs"
			px="sm"
			c="dark.4"
			fz="md"
			data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_PROVENANCE_LINE}
		>
			<IconSparkles size={16} stroke={1.5} aria-hidden style={{ flexShrink: 0 }} />
			<span>
				{parts.map((part, index) => (
					<Fragment key={part.key}>
						{index > 0 && ' · '}
						{part.node}
					</Fragment>
				))}
			</span>
		</Group>
	);
};
